import { Directive, HostBinding, inject, Injector, Input } from '@angular/core';
import { FiRemoteStateFullDataSource } from '../../fi-items-list/fi-remote-state-full-data-source';
import { IChecklist2Parameter, IChecklistScheduleForList, IChecklistV2ForList } from '../checklist-v2';
import { ObjectTypes } from '../../types';
import { FiItemsListType } from '../../fi-items-list/fi-items-list-type';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklistActionsHelperService } from '@fi-shared/checklist/fi-checklist-actions-helper.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { FiChecklist2SyncService } from '@fi-shared/checklist/fi-checklist2-sync.service';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiChecklist2SideListOptionsHelperService } from '../fi-checklist2-side-list-options-helper.service';

@Directive()
export abstract class FiChecklist2ListBase extends FiRemoteStateFullDataSource<IChecklistV2ForList> {
    @Input() ids: number[];
    abstract defaultSortColumnName: string;
    abstract defaultSortDirection: boolean;
    abstract objectType: ObjectTypes;
    abstract itemsListType: FiItemsListType;

    abstract actions: IItemsListAction2<IChecklistV2ForList>[];

    abstract auditMode: boolean;

    protected fiChecklistHelperService = inject(FiChecklistHelperService);
    protected fiChecklistActionsHelperService = inject(FiChecklistActionsHelperService);
    protected fiChecklist2Service = inject(FiChecklist2Service);
    protected fiChecklist2TemplateService = inject(FiChecklist2TemplateService);
    protected fiChecklist2SyncService = inject(FiChecklist2SyncService);
    protected fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    protected fiChecklist2SideListOptionsHelperService = inject(FiChecklist2SideListOptionsHelperService);

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    protected getItemsListParameter(loadArgs: ILoadDataArgs): IChecklist2Parameter {
        const parameter = super.getItemsListParameter(loadArgs) as IChecklist2Parameter;
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        if (this.ids) {
            parameter.Ids = this.ids;
        }
        return parameter;
    }

    protected showResultSnackbar(textMessage: string): void {
        this.snackBar.showSnackbar({ textMessage });
    }

    protected subscribeOnChecklistAnswersChangeForList(args: {
        loadChecklistsFn: (checklistToScheduleIds: number[]) => Promise<IChecklistV2ForList[]>;
    }): void {
        this.fiChecklist2SyncService.subscribeOnChecklistAnswersChangeForList({
            getCheckListsFn: () => this.items(),
            ngOnDestroy$: this.ngOnDestroy$,
            callback: (checklistToScheduleIds) => {
                // remove existing items, add new
                args.loadChecklistsFn(checklistToScheduleIds).then((newChecklists) => {
                    const items = this.items();
                    const checklistsToRemove = items.filter((checklist) =>
                        checklistToScheduleIds.includes(checklist.ChecklistToScheduleId),
                    );
                    this.removeData(checklistsToRemove);
                    this.appendData(newChecklists);
                    const sortedItems = [...items.sort(propComparator(this.sortColumn, this.sortDirection))];
                    this.setItems(sortedItems);
                });
            },
        });
    }

    protected onRowClick(item: IChecklistV2ForList): void {
        this.fiChecklistHelperService.onChecklistListItemClick(item, this.auditMode);
    }
}
